import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import {
  useCallback
} from 'react';

import type {
  ContractSigningCompletedCallbackParams,
  GetContractLanguagesResponse,
  GetContractSigningLinkResponse,
  GetSignedContractFilesResponse,
  ProblemDetails,
  SigningCompletedModel,
  VerifySignatureModel
} from '../../models';

import { useCustomWelcomeClient } from '../../custom-client.tsx';
import type { ErrorType } from '../../custom-client.tsx';

type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;




const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Returns a list of available languages for the contract<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useGetContractLanguagesHook = () => {
        const getContractLanguages = useCustomWelcomeClient<GetContractLanguagesResponse>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return getContractLanguages(
          {url: `/requests/${id}/contracts/languages`, method: 'GET', signal
    },
          );
        }, [getContractLanguages])
      }




export const getGetContractLanguagesQueryKey = (id: string,) => {
    return [
    `/requests/${id}/contracts/languages`
    ] as const;
    }


export const useGetContractLanguagesQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>, TError = ErrorType<ProblemDetails>>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContractLanguagesQueryKey(id);

  const getContractLanguages =  useGetContractLanguagesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>> = ({ signal }) => getContractLanguages(id, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetContractLanguagesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>>
export type GetContractLanguagesQueryError = ErrorType<ProblemDetails>


export function useGetContractLanguages<TData = Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetContractLanguages<TData = Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetContractLanguages<TData = Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetContractLanguages<TData = Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetContractLanguagesHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetContractLanguagesQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Returns a list of files of a signed contract<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useGetSignedContractFilesHook = () => {
        const getSignedContractFiles = useCustomWelcomeClient<GetSignedContractFilesResponse>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return getSignedContractFiles(
          {url: `/requests/${id}/contracts/signed`, method: 'GET', signal
    },
          );
        }, [getSignedContractFiles])
      }




export const getGetSignedContractFilesQueryKey = (id: string,) => {
    return [
    `/requests/${id}/contracts/signed`
    ] as const;
    }


export const useGetSignedContractFilesQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>, TError = ErrorType<ProblemDetails>>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSignedContractFilesQueryKey(id);

  const getSignedContractFiles =  useGetSignedContractFilesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>> = ({ signal }) => getSignedContractFiles(id, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetSignedContractFilesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>>
export type GetSignedContractFilesQueryError = ErrorType<ProblemDetails>


export function useGetSignedContractFiles<TData = Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSignedContractFiles<TData = Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSignedContractFiles<TData = Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetSignedContractFiles<TData = Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSignedContractFilesHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetSignedContractFilesQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Returns the content of a file of a signed contract <br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useDownloadContractFileContentHook = () => {
        const downloadContractFileContent = useCustomWelcomeClient<Blob>();

        return useCallback((
    id: string,
    fileid: string,
 signal?: AbortSignal
) => {


        return downloadContractFileContent(
          {url: `/requests/${id}/contracts/signed/${fileid}/download`, method: 'GET',
        responseType: 'blob', signal
    },
          );
        }, [downloadContractFileContent])
      }




export const getDownloadContractFileContentQueryKey = (id: string,
    fileid: string,) => {
    return [
    `/requests/${id}/contracts/signed/${fileid}/download`
    ] as const;
    }


export const useDownloadContractFileContentQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>, TError = ErrorType<ProblemDetails>>(id: string,
    fileid: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadContractFileContentQueryKey(id,fileid);

  const downloadContractFileContent =  useDownloadContractFileContentHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>> = ({ signal }) => downloadContractFileContent(id,fileid, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && fileid !== null && fileid !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type DownloadContractFileContentQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>>
export type DownloadContractFileContentQueryError = ErrorType<ProblemDetails>


export function useDownloadContractFileContent<TData = Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    fileid: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadContractFileContent<TData = Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    fileid: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadContractFileContent<TData = Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    fileid: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useDownloadContractFileContent<TData = Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    fileid: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadContractFileContentHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useDownloadContractFileContentQueryOptions(id,fileid,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Returns a zip of files of a signed contract<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useDownloadContractFilesZipContentHook = () => {
        const downloadContractFilesZipContent = useCustomWelcomeClient<Blob>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return downloadContractFilesZipContent(
          {url: `/requests/${id}/contracts/signed/download`, method: 'GET',
        responseType: 'blob', signal
    },
          );
        }, [downloadContractFilesZipContent])
      }




export const getDownloadContractFilesZipContentQueryKey = (id: string,) => {
    return [
    `/requests/${id}/contracts/signed/download`
    ] as const;
    }


export const useDownloadContractFilesZipContentQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>, TError = ErrorType<ProblemDetails>>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadContractFilesZipContentQueryKey(id);

  const downloadContractFilesZipContent =  useDownloadContractFilesZipContentHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>> = ({ signal }) => downloadContractFilesZipContent(id, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type DownloadContractFilesZipContentQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>>
export type DownloadContractFilesZipContentQueryError = ErrorType<ProblemDetails>


export function useDownloadContractFilesZipContent<TData = Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadContractFilesZipContent<TData = Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadContractFilesZipContent<TData = Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useDownloadContractFilesZipContent<TData = Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadContractFilesZipContentHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useDownloadContractFilesZipContentQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Callback url for docusign service
 */
export const useContractSigningCompletedCallbackHook = () => {
        const contractSigningCompletedCallback = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    signingCompletedModel: SigningCompletedModel,
    params: ContractSigningCompletedCallbackParams,
 signal?: AbortSignal
) => {


        return contractSigningCompletedCallback(
          {url: `/requests/${id}/contracts/signing-callback`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: signingCompletedModel,
        params, signal
    },
          );
        }, [contractSigningCompletedCallback])
      }




export const useContractSigningCompletedCallbackMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useContractSigningCompletedCallbackHook>>>, TError,ContractSigningCompletedCallbackMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useContractSigningCompletedCallbackHook>>>, TError,ContractSigningCompletedCallbackMutationVariables, TContext> => {

const mutationKey = ['contractSigningCompletedCallback'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const contractSigningCompletedCallback =  useContractSigningCompletedCallbackHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useContractSigningCompletedCallbackHook>>>, ContractSigningCompletedCallbackMutationVariables> = (props) => {
          const {id,data,params} = props ?? {};

          return  contractSigningCompletedCallback(id,data,params,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ContractSigningCompletedCallbackMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useContractSigningCompletedCallbackHook>>>>
    export type ContractSigningCompletedCallbackMutationBody = SigningCompletedModel
    export type ContractSigningCompletedCallbackMutationError = ErrorType<ProblemDetails>
    export type ContractSigningCompletedCallbackMutationVariables = {id: string;data: SigningCompletedModel;params: ContractSigningCompletedCallbackParams}

    export const useContractSigningCompletedCallback = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useContractSigningCompletedCallbackHook>>>, TError,ContractSigningCompletedCallbackMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useContractSigningCompletedCallbackHook>>>,
        TError,
        ContractSigningCompletedCallbackMutationVariables,
        TContext
      > => {
      return useMutation(useContractSigningCompletedCallbackMutationOptions(options), queryClient);
    }
    /**
 * Returns a link to sign the contract
 */
export const useGetContractSigningLinkHook = () => {
        const getContractSigningLink = useCustomWelcomeClient<GetContractSigningLinkResponse>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return getContractSigningLink(
          {url: `/requests/${id}/contracts/signing-link`, method: 'GET', signal
    },
          );
        }, [getContractSigningLink])
      }




export const getGetContractSigningLinkQueryKey = (id: string,) => {
    return [
    `/requests/${id}/contracts/signing-link`
    ] as const;
    }


export const useGetContractSigningLinkQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>, TError = ErrorType<ProblemDetails>>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContractSigningLinkQueryKey(id);

  const getContractSigningLink =  useGetContractSigningLinkHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>> = ({ signal }) => getContractSigningLink(id, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetContractSigningLinkQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>>
export type GetContractSigningLinkQueryError = ErrorType<ProblemDetails>


export function useGetContractSigningLink<TData = Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetContractSigningLink<TData = Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetContractSigningLink<TData = Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetContractSigningLink<TData = Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetContractSigningLinkHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetContractSigningLinkQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Verify the signature of a contract<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useVerifyContractSignatureHook = () => {
        const verifyContractSignature = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    verifySignatureModel: VerifySignatureModel,
 signal?: AbortSignal
) => {


        return verifyContractSignature(
          {url: `/requests/${id}/contracts/verify-signature`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: verifySignatureModel, signal
    },
          );
        }, [verifyContractSignature])
      }




export const useVerifyContractSignatureMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useVerifyContractSignatureHook>>>, TError,VerifyContractSignatureMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useVerifyContractSignatureHook>>>, TError,VerifyContractSignatureMutationVariables, TContext> => {

const mutationKey = ['verifyContractSignature'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const verifyContractSignature =  useVerifyContractSignatureHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useVerifyContractSignatureHook>>>, VerifyContractSignatureMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  verifyContractSignature(id,data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type VerifyContractSignatureMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useVerifyContractSignatureHook>>>>
    export type VerifyContractSignatureMutationBody = VerifySignatureModel
    export type VerifyContractSignatureMutationError = ErrorType<ProblemDetails>
    export type VerifyContractSignatureMutationVariables = {id: string;data: VerifySignatureModel}

    export const useVerifyContractSignature = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useVerifyContractSignatureHook>>>, TError,VerifyContractSignatureMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useVerifyContractSignatureHook>>>,
        TError,
        VerifyContractSignatureMutationVariables,
        TContext
      > => {
      return useMutation(useVerifyContractSignatureMutationOptions(options), queryClient);
    }
